import { Box, Grid, styled } from '@mui/material';
import { StyledLink } from 'FeatureModules/Activity/Components/Forms/styles';
import RawMaterialSelection from 'FeatureModules/ComponentChain/Components/Forms/RawMaterialSelection';
import useDialog from 'hooks/useDialog';
import { ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { useField } from 'formik';
import { TextField } from 'formik-mui';
import React, { FC } from 'react';
import { IBaseSite } from 'FeatureModules/Site/types/site.types';
import ExtendedFarmForm from './Forms/ExtendedFarmForm';

interface IFarmFormProps {
  disableRawMaterial?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
  background: theme.custom.themeColors.grayScale[10],
  borderRadius: 6,
  padding: theme.spacing(2),
}));

const RequiredContainer = styled(Box)(({ theme }) => ({
  background: '#fff',
  borderRadius: 6,
  padding: theme.spacing(2),
}));

export const SubTitle = styled(ThemeTypography)(({ theme }) => ({
  textTransform: 'uppercase',
  marginBottom: theme.spacing(1.5),
}));

export const StyledTextField = styled(TextField)(() => ({
  '&.MuiTextField-root': {
    '& .MuiInputBase-input': {
      fontSize: 12,
    },
  },
}));

const FarmForm: FC<IFarmFormProps> = ({ disableRawMaterial }) => {
  const { closeDialog } = useDialog();
  const [{ value: selectedPartner }] = useField<IBaseSite['ownedBy'] | null>('ownedBy');

  const handleNavigateAndCloseDialog = () => closeDialog('ADD_EDIT_SITE');

  return (
    <Container>
      <Box display="flex" alignItems="center" mb={2}>
        <Icon name="farm" size="large" color="gray-60" />
        <Box ml={0.5} />
        <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_60">
          Farm data
        </ThemeTypography>
        <Box ml={1} />
        <Icon name="info" size="large" />
        <Box ml={0.5} />
        <ThemeTypography variant="BODY_MEDIUM">
          Farms can also be imported in bulk in the{' '}
          {selectedPartner ? (
            <StyledLink
              to={`/partners/${selectedPartner.id}/imports`}
              onClick={handleNavigateAndCloseDialog}
            >
              Origin data import
            </StyledLink>
          ) : (
            'Origin data import'
          )}{' '}
          for a specific partner.
        </ThemeTypography>
      </Box>
      <RequiredContainer>
        <SubTitle variant="BODY_MEDIUM">Required farm data</SubTitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RawMaterialSelection
              isRequired
              label="Focus material"
              multiple={false}
              name="farmData.rawMaterialId"
              size="small"
              labelVariant="medium"
              disabled={disableRawMaterial}
              helperText="Select the focus material of the farm. This indicates the primary raw material output (e.g. harvested crop)."
            />
          </Grid>
        </Grid>
      </RequiredContainer>
      <Box mt={2} />
      <ExtendedFarmForm />
    </Container>
  );
};

export default FarmForm;
